/**
 * Zustand Gym Store
 *
 * Global state management for gym-related data.
 * Stores the currently selected gym.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Gym } from '@flash/shared';

/**
 * Gym store state interface
 */
interface GymState {
  /** Currently selected/viewed gym */
  selectedGym: Gym | null;
  /** ID of the currently selected gym */
  selectedGymId: string | null;
  /** ID of the gym the user is currently at (active gym for climbing) */
  activeGymId: string | null;
  /** Last visited path for returning users */
  lastVisitedPath: string | null;
}

/**
 * Gym store actions interface
 */
interface GymActions {
  /** Set the currently selected gym */
  setSelectedGym: (gym: Gym | null) => void;
  /** Set selected gym by ID only (useful when gym data isn't loaded yet) */
  setSelectedGymId: (gymId: string | null) => void;
  /** Clear all gym selections (e.g., on logout) */
  clearGymSelection: () => void;
  /** Set the active gym ID (gym user is currently at for climbing) */
  setActiveGymId: (gymId: string | null) => void;
  /** Clear the active gym */
  clearActiveGym: () => void;
  /** Set the last visited path for returning users */
  setLastVisitedPath: (path: string | null) => void;
  /** Reset store to initial state */
  reset: () => void;
}

/**
 * Combined store type
 */
type GymStore = GymState & GymActions;

/**
 * Initial state for the gym store
 */
const initialState: GymState = {
  selectedGym: null,
  selectedGymId: null,
  activeGymId: null,
  lastVisitedPath: null,
};

/**
 * Zustand store for gym state management
 *
 * Features:
 * - Tracks currently selected gym for navigation context
 * - Persists selected gym ID to localStorage for page refresh handling
 *
 * @example
 * ```tsx
 * // In a component
 * const { selectedGym, setSelectedGym } = useGymStore();
 *
 * // Select a gym
 * setSelectedGym(gym);
 *
 * // Access selected gym
 * if (selectedGym) {
 *   console.log(`Viewing: ${selectedGym.name}`);
 * }
 * ```
 */
export const useGymStore = create<GymStore>()(
  persist(
    (set) => ({
      // Initial state
      ...initialState,

      // Actions
      setSelectedGym: (gym: Gym | null) => {
        set({
          selectedGym: gym,
          selectedGymId: gym?.id ?? null,
        });
      },

      setSelectedGymId: (gymId: string | null) => {
        set((state) => ({
          selectedGymId: gymId,
          // Clear selected gym if ID doesn't match
          selectedGym: state.selectedGym?.id === gymId ? state.selectedGym : null,
        }));
      },

      clearGymSelection: () => {
        set({
          selectedGym: null,
          selectedGymId: null,
        });
      },

      setActiveGymId: (gymId: string | null) => {
        set({ activeGymId: gymId });
      },

      clearActiveGym: () => {
        set({ activeGymId: null });
      },

      setLastVisitedPath: (path: string | null) => {
        set({ lastVisitedPath: path });
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'flash-gym-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist IDs and paths to localStorage (gym data is fetched fresh)
      partialize: (state) => ({
        selectedGymId: state.selectedGymId,
        activeGymId: state.activeGymId,
        lastVisitedPath: state.lastVisitedPath,
      }),
    }
  )
);

/**
 * Selector for selected gym ID
 */
export const selectSelectedGymId = (state: GymStore): string | null => state.selectedGymId;

/**
 * Selector for selected gym
 */
export const selectSelectedGym = (state: GymStore): Gym | null => state.selectedGym;

/**
 * Selector for active gym ID (the gym user is currently at for climbing)
 */
export const selectActiveGymId = (state: GymStore): string | null => state.activeGymId;

/**
 * Selector for last visited path
 */
export const selectLastVisitedPath = (state: GymStore): string | null => state.lastVisitedPath;
